import { InteractionStatus, InteractionRequiredAuthError } from '@azure/msal-browser'
import { useMsal } from '@azure/msal-react'
import { useEffect, useState } from 'react'
import { apiScopes } from './auth'
import type { AppConfig } from './config'

/**
 * Sign in.
 *
 * Redirect rather than popup: the tenant holds the origin as the Admin app's redirect URI, a
 * popup is the first thing a browser blocks, and an Admin signing in once a day gains nothing
 * from staying on the page while it happens.
 */
export function SignInPrompt({ config }: { config: AppConfig }) {
  const { instance, inProgress } = useMsal()
  const [failure, setFailure] = useState<string | null>(null)

  return (
    <div className="mt-6">
      <button
        type="button"
        className="rounded-md bg-green-800 px-6 py-3 font-bold text-sand-050 hover:bg-green-700 disabled:opacity-55"
        disabled={inProgress !== InteractionStatus.None}
        onClick={() => {
          setFailure(null)
          // The API scope is asked for here as well as at token time, so consent is given
          // once, at sign-in, rather than surfacing later as a silent-acquisition failure.
          instance
            .loginRedirect({ scopes: apiScopes(config) })
            .catch((error: unknown) => setFailure(String(error)))
        }}
      >
        Sign in
      </button>

      {failure && <p className="mt-3 border-l-2 border-red-700 pl-3 text-red-700">{failure}</p>}
    </div>
  )
}

/**
 * What this sign-in is worth, as diagnostics.
 *
 * Who is signed in and the way out both live in the masthead's account menu; what belongs
 * here is the thing a menu is the wrong place for -- whether the API will actually take this
 * token, which is a question with a slow answer and a long one.
 */
export function SessionDetails({ config }: { config: AppConfig }) {
  const { accounts } = useMsal()
  const account = accounts[0]

  if (!account) {
    return null
  }

  return (
    <div className="mt-6 space-y-4">
      <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-6 gap-y-1 rounded-lg border border-ink-100 bg-card p-6">
        {/* Who is signed in and what roles the token claims are in the masthead's account
            menu; repeating them here would be two places to read and one to update. What
            belongs here is the question a menu is the wrong shape for. */}
        <dt className="whitespace-nowrap text-muted">API token</dt>
        <dd className="break-words">
          <ApiToken config={config} />
        </dd>
      </dl>
    </div>
  )
}

/**
 * Whether an access token for the API can actually be had.
 *
 * Signing in and calling the API are two different grants, and only the second one is what the
 * screens will need. Failing here while the sign-in above succeeded points at the scope or at
 * consent, not at the account -- which is worth knowing before a screen exists to discover it.
 */
function ApiToken({ config }: { config: AppConfig }) {
  const { instance, accounts } = useMsal()
  const [state, setState] = useState<'asking' | 'granted' | string>('asking')
  const account = accounts[0]

  useEffect(() => {
    if (!account) return
    let live = true

    instance
      .acquireTokenSilent({ scopes: apiScopes(config), account })
      .then(() => {
        if (live) setState('granted')
      })
      .catch((error: unknown) => {
        if (!live) return
        // Consent, MFA or an expired session: the tenant is asking for the user, and silence
        // is exactly what it will not accept. Anything else is a real failure.
        setState(
          error instanceof InteractionRequiredAuthError
            ? 'the tenant wants to ask you something -- sign out and in again'
            : String(error),
        )
      })

    return () => {
      live = false
    }
  }, [instance, account, config])

  if (state === 'asking') return <span className="text-muted">asking...</span>
  if (state === 'granted') return <span>granted for {config.apiScope}</span>
  return <span className="text-red-700">{state}</span>
}
